import { useMemo } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Sparkles, CloudRain, Sun, Cloud, Snowflake, Wind, Thermometer } from "lucide-react";

interface NarrativeProps {
  weather: any;
  aqi: any;
}

function celsiusToF(c: number) { return Math.round(c * 9 / 5 + 32); }

export function WeatherNarrative({ weather, aqi }: NarrativeProps) {
  const narrative = useMemo(() => {
    if (!weather?.current) return null;
    const c = weather.current;
    const daily = weather.daily?.[0];
    const alerts = weather.alerts || [];

    const temp = Math.round(c.temp);
    const feels = Math.round(c.feels_like);
    const humidity = c.humidity;
    const wind = Math.round(c.wind_speed);
    const condition = c.weather?.[0]?.main?.toLowerCase() || "";
    const desc = c.weather?.[0]?.description || "";

    const parts: string[] = [];

    // Main condition
    if (condition.includes("rain") || condition.includes("drizzle")) {
      parts.push(`Rain expected${temp > 0 ? "" : " with cold temperatures"}`);
    } else if (condition.includes("snow")) {
      parts.push("Snowfall in the area");
    } else if (condition.includes("thunderstorm")) {
      parts.push("Thunderstorm activity detected");
    } else if (condition.includes("clear")) {
      parts.push(temp > 30 ? "Hot and sunny conditions" : temp > 20 ? "Clear and pleasant skies" : "Clear skies");
    } else if (condition.includes("cloud")) {
      parts.push(temp > 25 ? "Warm with overcast skies" : "Mostly cloudy conditions");
    } else if (condition.includes("fog") || condition.includes("mist")) {
      parts.push("Reduced visibility due to fog");
    } else {
      parts.push(desc.charAt(0).toUpperCase() + desc.slice(1));
    }

    // Temperature context
    if (daily) {
      const hi = Math.round(daily.temp.max);
      const lo = Math.round(daily.temp.min);
      parts.push(`high of ${hi}°C, low of ${lo}°C`);
    } else {
      parts.push(`currently ${temp}°C`);
    }

    // Feels like delta
    if (Math.abs(feels - temp) >= 3) {
      parts.push(`feels like ${feels}°C`);
    }

    // Wind
    if (wind > 40) {
      parts.push("strong winds");
    } else if (wind > 20) {
      parts.push("breezy conditions");
    }

    // Humidity
    if (humidity > 80) {
      parts.push("high humidity");
    }

    // AQI
    if (aqi) {
      const aqiVal = aqi.list?.[0]?.main?.aqi;
      if (aqiVal >= 4) parts.push("poor air quality");
      else if (aqiVal >= 3) parts.push("moderate air quality");
    }

    // Alert mention
    if (alerts.length > 0) {
      parts.push(`${alerts.length} active ${alerts.length === 1 ? "alert" : "alerts"}`);
    }

    return parts.join(" • ");
  }, [weather, aqi]);

  if (!narrative) return null;

  return (
    <GlassPanel intensity="low" className="p-3.5">
      <div className="flex items-start gap-2.5">
        <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground/85 leading-relaxed">{narrative}</p>
      </div>
    </GlassPanel>
  );
}